import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Search, Dumbbell, Target, ChevronRight, Heart, FolderPlus, X, Check, Play, Layers } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { hapticFeedback } from '../utils/mobile';
import FeatureDisclaimer from './FeatureDisclaimer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AnimatedExercise = ({ imageStart, imageEnd, name }) => {
  const [loaded, setLoaded] = useState(false);

  if (!imageStart) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-100 dark:bg-gray-800">
        <Dumbbell size={48} className="text-gray-300 dark:text-gray-600 dark:text-gray-300" />
      </div>
    );
  }

  return (
    <div className="relative w-full h-full bg-white dark:bg-gray-800 overflow-hidden flex items-center justify-center">
      <img src={imageStart} alt={`${name} - muscle illustration`}
        className="w-full h-full object-contain transition-opacity duration-300"
        style={{ opacity: loaded ? 1 : 0 }} onLoad={() => setLoaded(true)} loading="lazy" />
      {!loaded && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#FF6600]"></div>
        </div>
      )}
    </div>
  );
};

const VideoModal = ({ exerciseId, exerciseName, token, onClose }) => {
  const [videos, setVideos] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchVideo = async () => {
      try {
        const res = await axios.get(`${API}/exercise/video/${exerciseId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const videoList = res.data.videos || [];
        if (videoList.length > 0) {
          setVideos(videoList);
          setCurrentIndex(0);
        } else {
          setError('No demonstration video found for this exercise.');
        }
      } catch (e) {
        setError('Could not load video. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    fetchVideo();
  }, [exerciseId, token]);

  const tryNextVideo = () => {
    if (currentIndex + 1 < videos.length) {
      setCurrentIndex(prev => prev + 1);
    } else {
      setError('No embeddable video found. Try searching YouTube directly.');
      setVideos([]);
    }
  };

  const currentVideo = videos[currentIndex];

  return (
    <div className="fixed inset-0 bg-black/80 z-[99999] flex items-center justify-center p-4" onClick={onClose} data-testid="video-modal">
      <div className="bg-white dark:bg-gray-900 rounded-2xl w-full max-w-2xl overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="bg-black p-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-white min-w-0">
            <Play size={18} className="text-[#FF6600] flex-shrink-0" />
            <span className="font-semibold text-sm truncate">{exerciseName} — Anatomy 3D</span>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white p-1" data-testid="video-modal-close">
            <X size={20} />
          </button>
        </div>
        <div className="aspect-video bg-black relative">
          {loading && (
            <div className="w-full h-full flex items-center justify-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#FF6600]"></div>
            </div>
          )}
          {error && !currentVideo && (
            <div className="w-full h-full flex items-center justify-center text-center p-6">
              <div>
                <Dumbbell size={40} className="mx-auto text-gray-500 dark:text-gray-400 mb-3" />
                <p className="text-gray-400 text-sm">{error}</p>
                <a
                  href={`https://www.youtube.com/results?search_query=${encodeURIComponent(exerciseName)}+3D+anatomy+muscle+animation`}
                  target="_blank" rel="noopener noreferrer"
                  className="inline-block mt-3 text-[#FF6600] text-sm font-semibold hover:underline"
                  data-testid="youtube-search-link"
                >
                  Search on YouTube
                </a>
              </div>
            </div>
          )}
          {currentVideo && !loading && (
            <iframe
              key={currentVideo.id}
              src={`https://www.youtube.com/embed/${currentVideo.id}?autoplay=1&rel=0`}
              title={currentVideo.title || exerciseName}
              className="w-full h-full"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              onError={tryNextVideo}
              data-testid="youtube-player"
            />
          )}
        </div>
        {currentVideo && !loading && (
          <div className="p-3 bg-gray-50 dark:bg-gray-900 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate mb-1" data-testid="video-title">{currentVideo.title}</p>
            {videos.length > 1 && (
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-400">{currentIndex + 1} / {videos.length}</span>
                <div className="flex gap-2">
                  {currentIndex > 0 && (
                    <button onClick={() => setCurrentIndex(prev => prev - 1)}
                      className="text-xs text-[#FF6600] font-semibold hover:underline" data-testid="prev-video-btn">
                      Previous
                    </button>
                  )}
                  {currentIndex + 1 < videos.length && (
                    <button onClick={() => setCurrentIndex(prev => prev + 1)}
                      className="text-xs text-[#FF6600] font-semibold hover:underline" data-testid="next-video-btn">
                      Next Video
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const ExerciseLibrary = ({ token }) => {
  const [exercises, setExercises] = useState([]);
  const [filteredExercises, setFilteredExercises] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [favorites, setFavorites] = useState(new Set());
  const [activeTab, setActiveTab] = useState('all');
  const [customLists, setCustomLists] = useState([]);
  const [showListModal, setShowListModal] = useState(false);
  const { t } = useTranslation();
  const [newListName, setNewListName] = useState('');
  const [selectedForList, setSelectedForList] = useState(new Set());
  const [addToListMode, setAddToListMode] = useState(false);
  const [targetListId, setTargetListId] = useState(null);
  const [videoExercise, setVideoExercise] = useState(null);

  const categories = ['all', 'chest', 'back', 'legs', 'shoulders', 'arms', 'core', 'cardio', 'stretching', 'full body', 'biceps', 'triceps', 'calves', 'hamstrings', 'quadriceps', 'glutes'];
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchExercises(); fetchFavorites(); fetchCustomLists(); }, []);

  const filterExercises = useCallback(() => {
    let source = exercises;
    if (activeTab === 'favorites') {
      source = exercises.filter(ex => favorites.has(ex.id));
    }
    let filtered = source;
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(ex => ex.muscle_group === selectedCategory);
    }
    if (searchTerm) {
      filtered = filtered.filter(ex =>
        ex.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ex.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    setFilteredExercises(filtered);
  }, [exercises, selectedCategory, searchTerm, favorites, activeTab]);

  useEffect(() => { filterExercises(); }, [filterExercises]);

  const fetchExercises = async () => {
    try {
      const response = await axios.get(`${API}/exercise/library`, { headers });
      setExercises(response.data.exercises || []);
    } catch (error) { console.error(error); }
    finally { setLoading(false); }
  };

  const fetchFavorites = async () => {
    try {
      const response = await axios.get(`${API}/exercise/favorites`, { headers });
      setFavorites(new Set(response.data.favorites || []));
    } catch (error) { console.error(error); }
  };

  const fetchCustomLists = async () => {
    try {
      const res = await axios.get(`${API}/exercise/custom-lists`, { headers });
      setCustomLists(res.data.lists || []);
    } catch (e) { console.error(e); }
  };

  const toggleFavorite = async (exerciseId) => {
    const isFav = favorites.has(exerciseId);
    try {
      if (isFav) {
        await axios.delete(`${API}/exercise/favorites/${exerciseId}`, { headers });
        setFavorites(prev => { const n = new Set(prev); n.delete(exerciseId); return n; });
      } else {
        await axios.post(`${API}/exercise/favorites/${exerciseId}`, {}, { headers });
        setFavorites(prev => new Set(prev).add(exerciseId));
      }
    } catch (e) { console.error(e); }
  };

  const createCustomList = async () => {
    if (!newListName.trim()) return;
    try {
      const exercises = Array.from(selectedForList).map(id => ({ exercise_id: id, sets: 3, reps: '10-12' }));
      await axios.post(`${API}/exercise/custom-lists`, { name: newListName, exercises }, { headers });
      setShowListModal(false);
      setNewListName('');
      setSelectedForList(new Set());
      setAddToListMode(false);
      fetchCustomLists();
    } catch (e) { console.error(e); }
  };

  const addToExistingList = async (listId) => {
    try {
      const listRes = await axios.get(`${API}/exercise/custom-lists/${listId}`, { headers });
      const existing = listRes.data.list.exercises || [];
      const newExercises = Array.from(selectedForList).map(id => ({ exercise_id: id, sets: 3, reps: '10-12' }));
      await axios.put(`${API}/exercise/custom-lists/${listId}`, { exercises: [...existing, ...newExercises] }, { headers });
      setAddToListMode(false);
      setSelectedForList(new Set());
      setTargetListId(null);
      fetchCustomLists();
    } catch (e) { console.error(e); }
  };

  const deleteList = async (listId) => {
    try {
      await axios.delete(`${API}/exercise/custom-lists/${listId}`, { headers });
      fetchCustomLists();
    } catch (e) { console.error(e); }
  };

  const toggleSelectForList = (id) => {
    setSelectedForList(prev => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  if (loading) {
    return (
      <div className="text-center py-16" data-testid="exercise-library-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF6600] mx-auto"></div>
        <p className="mt-4 text-gray-600 dark:text-gray-300">{t('exerciseLib.loading')}</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto pb-8" data-testid="exercise-library">
      {/* Disclaimer */}
      <div className="mb-4">
        <FeatureDisclaimer type="exercise" />
      </div>
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm mb-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-bold flex items-center gap-2 text-gray-900 dark:text-white">
            <Dumbbell size={20} className="text-[#FF6600]" />
            {t('exerciseLib.title')}
          </h2>
          <div className="flex gap-2">
            {addToListMode ? (
              <>
                <button onClick={() => { setAddToListMode(false); setSelectedForList(new Set()); }}
                  className="text-xs text-gray-600 dark:text-gray-300 px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600" data-testid="cancel-select-btn">Cancel</button>
                <button onClick={() => setShowListModal(true)} disabled={selectedForList.size === 0}
                  className="text-xs bg-[#FF6600] text-white px-3 py-1.5 rounded-lg font-semibold disabled:opacity-40" data-testid="save-to-list-btn">
                  Save {selectedForList.size} to List
                </button>
              </>
            ) : (
              <button onClick={() => setAddToListMode(true)}
                className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-3 py-1.5 rounded-lg font-semibold flex items-center gap-1"
                data-testid="select-for-list-btn">
                <FolderPlus size={14} /> {t('exerciseLib.createList')}
              </button>
            )}
          </div>
        </div>

        {/* Tabs: All / Favorites / My Lists */}
        <div className="flex gap-1 bg-gray-100 dark:bg-gray-700/50 rounded-xl p-1 mb-4">
          {[
            { id: 'all', label: `All (${exercises.length})` },
            { id: 'favorites', label: `${t('exerciseLib.favorites')} (${favorites.size})` },
            { id: 'lists', label: `${t('exerciseLib.myLists')} (${customLists.length})` },
          ].map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-colors ${activeTab === t.id ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm' : 'text-gray-600 dark:text-gray-300'}`}
              data-testid={`tab-${t.id}`}>
              {t.label}
            </button>
          ))}
        </div>

        {activeTab !== 'lists' && (
          <>
            <div className="flex flex-col md:flex-row gap-4 mb-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-3 text-gray-400" size={20} />
                <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={t('exerciseLib.search')} data-testid="search-input"
                  className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 dark:border-gray-700 dark:border-gray-600 rounded-lg focus:outline-none focus:border-[#FF6600] bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400" />
              </div>
              <div className="flex gap-2 overflow-x-auto pb-1">
                {categories.map(cat => (
                  <button key={cat} onClick={() => setSelectedCategory(cat)} data-testid={`category-${cat}`}
                    className={`px-4 py-2 rounded-lg whitespace-nowrap font-semibold text-sm transition-colors ${selectedCategory === cat ? 'bg-[#FF6600] text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'}`}>
                    {cat.charAt(0).toUpperCase() + cat.slice(1)}
                  </button>
                ))}
              </div>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-300" data-testid="results-count">
              Showing {filteredExercises.length} exercises
            </p>
          </>
        )}
      </div>

      {/* My Lists Tab */}
      {activeTab === 'lists' && (
        <div className="space-y-3">
          {customLists.length === 0 ? (
            <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-xl">
              <FolderPlus size={48} className="mx-auto text-gray-300 dark:text-gray-600 dark:text-gray-300 mb-4" />
              <h3 className="font-bold text-gray-900 dark:text-white mb-2">No Custom Lists Yet</h3>
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">Select exercises and save them to a custom workout list</p>
              <button onClick={() => { setActiveTab('all'); setAddToListMode(true); }}
                className="bg-[#FF6600] text-white px-6 py-2.5 rounded-lg font-semibold" data-testid="create-first-list-btn">
                Create Your First List
              </button>
            </div>
          ) : (
            customLists.map(list => (
              <div key={list.id} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4" data-testid={`custom-list-${list.id}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-bold text-gray-900 dark:text-white">{list.name}</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{list.exercises?.length || 0} exercises{list.description ? ` | ${list.description}` : ''}</p>
                  </div>
                  <button onClick={() => deleteList(list.id)} className="text-red-400 hover:text-red-600 p-1" data-testid={`delete-list-${list.id}`}>
                    <X size={16} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Exercise Grid */}
      {activeTab !== 'lists' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredExercises.map((exercise) => (
            <div key={exercise.id}
              className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden hover:shadow-md transition-shadow ${addToListMode && selectedForList.has(exercise.id) ? 'ring-2 ring-[#FF6600]' : ''}`}
              data-testid={`exercise-card-${exercise.id}`}
              onClick={addToListMode ? () => toggleSelectForList(exercise.id) : undefined}>
              <div className="relative aspect-video">
                <AnimatedExercise imageStart={exercise.image_start} imageEnd={exercise.image_end} name={exercise.name} />
                <div className="absolute top-2 right-2 px-2 py-0.5 bg-black/60 text-white text-xs rounded-full font-medium">
                  {exercise.difficulty}
                </div>
                {!addToListMode && (
                  <button onClick={(e) => { e.stopPropagation(); toggleFavorite(exercise.id); }}
                    className="absolute top-2 left-2 p-1.5 bg-black/40 rounded-full hover:bg-black/60 transition-colors"
                    data-testid={`fav-btn-${exercise.id}`}>
                    <Heart size={16} className={favorites.has(exercise.id) ? 'fill-red-500 text-red-500' : 'text-white'} />
                  </button>
                )}
                {addToListMode && (
                  <div className={`absolute top-2 left-2 w-6 h-6 rounded-full border-2 flex items-center justify-center ${selectedForList.has(exercise.id) ? 'bg-[#FF6600] border-[#FF6600]' : 'border-white bg-black/30'}`}>
                    {selectedForList.has(exercise.id) && <Check size={14} className="text-white" />}
                  </div>
                )}
                {/* Watch Video Button */}
                {!addToListMode && (
                  <button
                    onClick={(e) => { e.stopPropagation(); hapticFeedback('medium'); setVideoExercise(exercise); }}
                    className="absolute bottom-2 right-2 flex items-center gap-1.5 px-3 py-1.5 bg-[#FF6600] hover:bg-[#E55C00] text-white text-xs font-semibold rounded-full shadow-lg transition-all hover:scale-105"
                    data-testid={`play-video-${exercise.id}`}
                  >
                    <Play size={12} className="fill-white" />
                    Watch
                  </button>
                )}
              </div>

              <div className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-bold flex-1 text-gray-900 dark:text-white">{exercise.name}</h3>
                  <span className="px-2 py-1 bg-orange-100 dark:bg-orange-900/30 text-[#FF6600] rounded-full text-xs font-semibold ml-2">
                    {exercise.muscle_group}
                  </span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-3 line-clamp-2">{exercise.description}</p>
                <div className="flex gap-4 text-sm text-gray-600 dark:text-gray-300 mb-3">
                  <div className="flex items-center gap-1"><Target size={14} className="text-[#FF6600]" /><span>{exercise.difficulty}</span></div>
                  <div className="flex items-center gap-1"><Layers size={14} className="text-[#FF6600]" /><span>{exercise.sets || 3} x {exercise.reps || '10-12'}</span></div>
                </div>
                {exercise.equipment && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 dark:text-gray-500 dark:text-gray-400 mb-3"><span className="font-semibold">Equipment:</span> {exercise.equipment}</p>
                )}
                {exercise.instructions && exercise.instructions.length > 0 && !addToListMode && (
                  <button onClick={() => setExpandedId(expandedId === exercise.id ? null : exercise.id)}
                    className="text-xs text-[#FF6600] font-semibold flex items-center gap-1 hover:underline"
                    data-testid={`toggle-instructions-${exercise.id}`}>
                    {expandedId === exercise.id ? 'Hide' : 'Show'} Instructions
                    <ChevronRight size={12} className={`transition-transform ${expandedId === exercise.id ? 'rotate-90' : ''}`} />
                  </button>
                )}
                {expandedId === exercise.id && exercise.instructions && (
                  <ol className="mt-2 space-y-1 text-xs text-gray-600 dark:text-gray-300 list-decimal list-inside" data-testid={`instructions-${exercise.id}`}>
                    {exercise.instructions.map((step, i) => (<li key={i}>{step}</li>))}
                  </ol>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {activeTab !== 'lists' && filteredExercises.length === 0 && (
        <div className="text-center py-16" data-testid="no-exercises-found">
          <Dumbbell size={48} className="text-gray-300 dark:text-gray-600 dark:text-gray-300 mx-auto mb-4" />
          <h3 className="font-bold mb-2 text-gray-900 dark:text-white">
            {activeTab === 'favorites' ? t('exerciseLib.noResults') : t('exerciseLib.noResults')}
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            {activeTab === 'favorites' ? 'Heart an exercise to add it to your favorites' : 'Try adjusting your search or filter'}
          </p>
        </div>
      )}

      {/* Create List Modal */}
      {showListModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-md" data-testid="create-list-modal">
            <h3 className="font-bold text-lg text-gray-900 dark:text-white mb-4">Save to Workout List</h3>
            <input type="text" value={newListName} onChange={e => setNewListName(e.target.value)}
              placeholder="List name (e.g., Monday Arms)" data-testid="list-name-input"
              className="w-full p-3 border-2 border-gray-200 dark:border-gray-700 dark:border-gray-600 rounded-lg mb-4 bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />

            {customLists.length > 0 && (
              <div className="mb-4">
                <p className="text-sm font-semibold text-gray-600 dark:text-gray-300 mb-2">Or add to existing list:</p>
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {customLists.map(list => (
                    <button key={list.id} onClick={() => addToExistingList(list.id)}
                      className="w-full text-left p-2 rounded-lg bg-gray-50 dark:bg-gray-800 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-900 dark:text-white"
                      data-testid={`add-to-list-${list.id}`}>
                      {list.name} ({list.exercises?.length || 0} exercises)
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="flex gap-3">
              <button onClick={() => { setShowListModal(false); setNewListName(''); }}
                className="flex-1 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-semibold" data-testid="cancel-list-btn">Cancel</button>
              <button onClick={createCustomList} disabled={!newListName.trim()}
                className="flex-1 bg-[#FF6600] text-white py-2.5 rounded-lg font-semibold disabled:opacity-40" data-testid="confirm-create-list-btn">
                Create New List
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Video Modal */}
      {videoExercise && (
        <VideoModal
          exerciseId={videoExercise.id}
          exerciseName={videoExercise.name}
          token={token}
          onClose={() => setVideoExercise(null)}
        />
      )}
    </div>
  );
};

export default ExerciseLibrary;
